import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { parseElectionDate } from '../../../../lib/date-utils';
import { useGuide } from '../../hooks/useGuide';

function mapsUrl(address) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

function formatDate(dateStr) {
  return parseElectionDate(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  });
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 || 12;
  return m ? `${hour}:${String(m).padStart(2, '0')}${suffix}` : `${hour}${suffix}`;
}

export default function GuidePollingPlace() {
  const navigate = useNavigate();
  const { lookupResult } = useGuide();

  useEffect(() => {
    if (!lookupResult) navigate('/', { replace: true });
  }, [lookupResult, navigate]);

  if (!lookupResult) return null;

  const { pollingPlace, earlyVoting, election } = lookupResult;

  return (
    <div>
      <p className="g-label">Step 1 — Polling Place</p>
      <h1 className="g-h1">Where to vote.</h1>

      {pollingPlace ? (
        <div className="g-card" style={{ marginBottom: 16 }}>
          <p className="g-label" style={{ marginBottom: 4 }}>
            Election Day — {formatDate(election.electionDate)}
          </p>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>{pollingPlace.name}</div>
          <div style={{ fontSize: 15, color: '#aaa', marginBottom: 12 }}>
            {pollingPlace.address}
          </div>
          {pollingPlace.hours && (
            <div style={{ fontSize: 14, color: 'var(--g-muted)', marginBottom: 12 }}>
              Hours:{' '}
              {typeof pollingPlace.hours === 'object'
                ? JSON.stringify(pollingPlace.hours)
                : pollingPlace.hours}
            </div>
          )}
          <a
            href={mapsUrl(pollingPlace.address)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              background: 'var(--g-orange)',
              color: 'var(--g-black)',
              padding: '10px 20px',
              fontWeight: 900,
              fontSize: 15,
              textDecoration: 'none',
              borderRadius: 'var(--g-radius)',
            }}
          >
            Get directions →
          </a>
        </div>
      ) : (
        <div className="g-card" style={{ marginBottom: 16 }}>
          <p style={{ color: 'var(--g-muted)', fontSize: 15 }}>
            Election day polling place not yet available. Check back closer to election day.
          </p>
        </div>
      )}

      {earlyVoting?.locations?.length > 0 && (
        <>
          <p className="g-label" style={{ marginTop: 24 }}>
            Early voting locations
          </p>
          {earlyVoting.windows?.length > 0 && (
            <div className="g-card" style={{ marginBottom: 16 }}>
              <div
                style={{ fontSize: 14, fontWeight: 700, color: 'var(--g-muted)', marginBottom: 8 }}
              >
                Early voting period
              </div>
              {earlyVoting.windows.map((w) => (
                <div
                  key={`${w.startDate}-${w.endDate}`}
                  style={{ fontSize: 14, color: '#ccc', marginBottom: 4 }}
                >
                  {w.label ||
                    `${formatDate(w.startDate)}${w.startDate !== w.endDate ? ` – ${formatDate(w.endDate)}` : ''}${w.startTime ? `, ${formatTime(w.startTime)}–${formatTime(w.endTime)}` : ''}`}
                </div>
              ))}
            </div>
          )}
          {earlyVoting.locations.map((loc) => (
            <div className="g-card" key={loc.name} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>→ {loc.name}</div>
              <div style={{ fontSize: 14, color: '#aaa', marginBottom: 8 }}>{loc.address}</div>
              <a
                href={mapsUrl(loc.address)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 13, color: 'var(--g-orange)' }}
              >
                Directions →
              </a>
            </div>
          ))}
        </>
      )}

      <div style={{ marginTop: 32 }}>
        <button className="g-btn-primary" onClick={() => navigate('/guide/ballot-overview')}>
          See my ballot →
        </button>
      </div>
    </div>
  );
}
